import { ObjectValues } from 'types';
import { User } from './User';
import { Lead } from './Lead';
import { DealRoom } from './DealRoom';
import { NotificationMedium } from './Notification';

export const LEAD_NOTIFICATION_STATUS = {
  RECEIVED: 'received',
  OPENED: 'opened',
  FAILED: 'failed',
} as const;

export type LeadNotificationStatus = ObjectValues<
  typeof LEAD_NOTIFICATION_STATUS
>;

export type LeadNotification = {
  _id: string;

  typeCode: number;
  channel: NotificationMedium;
  status: LeadNotificationStatus;

  leadId: string;
  lead: Partial<Lead>;

  userId: string;
  user: Partial<User>;

  dealRoomId: string;
  dealRoom: Partial<DealRoom>;

  createdAt: Date;
  updatedAt: Date;
};

export const LEAD_NOTIFICATION_TYPE = {
  // EMAIL
  EMAIL_NDA_SENT: 'EMAIL_NDA_SENT',
  EMAIL_NDA_RESENT: 'EMAIL_NDA_RESENT',
  EMAIL_ACCEPTED_INTEREST: 'EMAIL_ACCEPTED_INTEREST',
  EMAIL_ADDED_TO_DEAL: 'EMAIL_ADDED_TO_DEAL',
  EMAIL_NEW_ACCESSIBLE_FILES: 'EMAIL_NEW_ACCESSIBLE_FILES',
  EMAIL_NEW_FAQ: 'EMAIL_NEW_FAQ',
  EMAIL_DATA_ROOM_ACCESS_APPROVED: 'EMAIL_DATA_ROOM_ACCESS_APPROVED',
  EMAIL_DATA_ROOM_ACCESS_DECLINED: 'EMAIL_DATA_ROOM_ACCESS_DECLINED',
  EMAIL_NUDGE: 'EMAIL_NUDGE',
  EMAIL_RFI_SUBMITTED: 'EMAIL_RFI_SUBMITTED',
  EMAIL_UNREAD_MESSAGES: 'EMAIL_UNREAD_MESSAGES',
  EMAIL_QUESTION_ANSWERED: 'EMAIL_QUESTION_ANSWERED',
  EMAIL_PENDING_FILES_REMINDER: 'EMAIL_PENDING_FILES_REMINDER',

  // SMS
  SMS_CONFIRM_FILE_INTEREST: 'SMS_CONFIRM_FILE_INTEREST',
  SMS_RFI_SUBMITTED: 'SMS_RFI_SUBMITTED',

  // PLATFORM
  PLATFORM_MOVED_TO_ACCEPTED: 'PLATFORM_MOVED_TO_ACCEPTED',
  PLATFORM_MOVED_TO_CONFIRMED: 'PLATFORM_MOVED_TO_CONFIRMED',
  PLATFORM_MOVED_TO_OFFER: 'PLATFORM_MOVED_TO_OFFER',
  PLATFORM_SELLER_ACCEPT_OFFER: 'PLATFORM_SELLER_ACCEPT_OFFER',
  PLATFORM_SELLER_REJECT_OFFER: 'PLATFORM_SELLER_REJECT_OFFER',
  PLATFORM_LISTING_SOLD: 'PLATFORM_LISTING_SOLD',
  PLATFORM_MESSAGE_FROM_SELLER: 'PLATFORM_MESSAGE_FROM_SELLER',
  PLATFORM_ACCESSIBLE_DOCUMENTS: 'PLATFORM_ACCESSIBLE_DOCUMENTS',
  PLATFORM_NDA_SENT: 'PLATFORM_NDA_SENT',
  PLATFORM_LEAD_PULSE_CHECK: 'PLATFORM_LEAD_PULSE_CHECK',
  PLATFORM_LEAD_ADDED_TO_DEAL: 'PLATFORM_LEAD_ADDED_TO_DEAL',
  PLATFORM_QUESTION_DRAFTED: 'PLATFORM_QUESTION_DRAFTED',
  PLATFORM_QUESTION_REJECTED_BUY_SIDE: 'PLATFORM_QUESTION_REJECTED_BUY_SIDE',
  PLATFORM_QUESTION_REJECTED_SELL_SIDE: 'PLATFORM_QUESTION_REJECTED_SELL_SIDE',
  PLATFORM_QUESTION_REPLIED: 'PLATFORM_QUESTION_REPLIED',
  PLATFORM_QUESTION_EDITED: 'PLATFORM_QUESTION_EDITED',
  PLATFORM_FILE_ACCESS: 'PLATFORM_FILE_ACCESS',
  PLATFORM_CONFIRM_FILES_ACCESS: 'PLATFORM_CONFIRM_FILES_ACCESS',
  PLATFORM_ENABLED_DATA_ROOM_ACCESS: 'PLATFORM_ENABLED_DATA_ROOM_ACCESS',
  PLATFORM_DOWNLOADED_FILE_EXPIRE_IN_TWO_DAYS:
    'PLATFORM_DOWNLOADED_FILE_EXPIRE_IN_TWO_DAYS',
  PLATFORM_PENDING_FILES_REMINDER: 'PLATFORM_PENDING_FILES_REMINDER',
} as const;

export const LEAD_NOTIFICATION_TYPE_CODE = {
  // EMAIL
  [LEAD_NOTIFICATION_TYPE.EMAIL_NDA_SENT]: 100,
  [LEAD_NOTIFICATION_TYPE.EMAIL_NDA_RESENT]: 101,
  [LEAD_NOTIFICATION_TYPE.EMAIL_ACCEPTED_INTEREST]: 102,
  [LEAD_NOTIFICATION_TYPE.EMAIL_ADDED_TO_DEAL]: 103,
  [LEAD_NOTIFICATION_TYPE.EMAIL_NEW_ACCESSIBLE_FILES]: 104,
  [LEAD_NOTIFICATION_TYPE.EMAIL_NEW_FAQ]: 105,
  [LEAD_NOTIFICATION_TYPE.EMAIL_DATA_ROOM_ACCESS_APPROVED]: 106,
  [LEAD_NOTIFICATION_TYPE.EMAIL_DATA_ROOM_ACCESS_DECLINED]: 107,
  [LEAD_NOTIFICATION_TYPE.EMAIL_NUDGE]: 108,
  [LEAD_NOTIFICATION_TYPE.EMAIL_RFI_SUBMITTED]: 109,
  [LEAD_NOTIFICATION_TYPE.EMAIL_UNREAD_MESSAGES]: 110,
  [LEAD_NOTIFICATION_TYPE.EMAIL_QUESTION_ANSWERED]: 112,
  [LEAD_NOTIFICATION_TYPE.EMAIL_PENDING_FILES_REMINDER]: 113,

  // SMS
  [LEAD_NOTIFICATION_TYPE.SMS_CONFIRM_FILE_INTEREST]: 200,
  [LEAD_NOTIFICATION_TYPE.SMS_RFI_SUBMITTED]: 201,

  // PLATFORM
  [LEAD_NOTIFICATION_TYPE.PLATFORM_MOVED_TO_ACCEPTED]: 300,
  [LEAD_NOTIFICATION_TYPE.PLATFORM_MOVED_TO_CONFIRMED]: 301,
  [LEAD_NOTIFICATION_TYPE.PLATFORM_MOVED_TO_OFFER]: 302,
  [LEAD_NOTIFICATION_TYPE.PLATFORM_SELLER_ACCEPT_OFFER]: 303,
  [LEAD_NOTIFICATION_TYPE.PLATFORM_SELLER_REJECT_OFFER]: 304,
  [LEAD_NOTIFICATION_TYPE.PLATFORM_LISTING_SOLD]: 305,
  [LEAD_NOTIFICATION_TYPE.PLATFORM_MESSAGE_FROM_SELLER]: 306,
  [LEAD_NOTIFICATION_TYPE.PLATFORM_ACCESSIBLE_DOCUMENTS]: 307,
  [LEAD_NOTIFICATION_TYPE.PLATFORM_NDA_SENT]: 308,
  [LEAD_NOTIFICATION_TYPE.PLATFORM_LEAD_PULSE_CHECK]: 309,
  [LEAD_NOTIFICATION_TYPE.PLATFORM_LEAD_ADDED_TO_DEAL]: 310,
  [LEAD_NOTIFICATION_TYPE.PLATFORM_QUESTION_DRAFTED]: 311,
  [LEAD_NOTIFICATION_TYPE.PLATFORM_QUESTION_REJECTED_BUY_SIDE]: 312,
  [LEAD_NOTIFICATION_TYPE.PLATFORM_QUESTION_REJECTED_SELL_SIDE]: 313,
  [LEAD_NOTIFICATION_TYPE.PLATFORM_QUESTION_REPLIED]: 314,
  [LEAD_NOTIFICATION_TYPE.PLATFORM_QUESTION_EDITED]: 315,
  [LEAD_NOTIFICATION_TYPE.PLATFORM_FILE_ACCESS]: 316,
  [LEAD_NOTIFICATION_TYPE.PLATFORM_CONFIRM_FILES_ACCESS]: 317,
  [LEAD_NOTIFICATION_TYPE.PLATFORM_ENABLED_DATA_ROOM_ACCESS]: 318,
  [LEAD_NOTIFICATION_TYPE.PLATFORM_DOWNLOADED_FILE_EXPIRE_IN_TWO_DAYS]: 319,
  [LEAD_NOTIFICATION_TYPE.PLATFORM_PENDING_FILES_REMINDER]: 320,
} as const;

export const LEAD_NOTIFICATIONS_TRANSLATIONS_RECEIVED = {
  // EMAIL
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_NDA_SENT]]:
    'EMAIL_NDA_SENT_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_NDA_RESENT]]:
    'EMAIL_NDA_RESENT_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_ACCEPTED_INTEREST]]:
    'EMAIL_ACCEPTED_INTEREST_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_ADDED_TO_DEAL]]:
    'EMAIL_ADDED_TO_DEAL_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.EMAIL_NEW_ACCESSIBLE_FILES
  ]]: 'EMAIL_NEW_ACCESSIBLE_FILES_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_NEW_FAQ]]:
    'EMAIL_NEW_FAQ_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.EMAIL_DATA_ROOM_ACCESS_APPROVED
  ]]: 'EMAIL_DATA_ROOM_ACCESS_APPROVED_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.EMAIL_DATA_ROOM_ACCESS_DECLINED
  ]]: 'EMAIL_DATA_ROOM_ACCESS_DECLINED_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_NUDGE]]:
    'EMAIL_NUDGE_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_RFI_SUBMITTED]]:
    'EMAIL_RFI_SUBMITTED_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_UNREAD_MESSAGES]]:
    'EMAIL_UNREAD_MESSAGES_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_QUESTION_ANSWERED]]:
    'EMAIL_QUESTION_ANSWERED_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.EMAIL_PENDING_FILES_REMINDER
  ]]: 'EMAIL_PENDING_FILES_REMINDER_RECEIVED',

  // SMS
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.SMS_CONFIRM_FILE_INTEREST
  ]]: 'SMS_CONFIRM_FILE_INTEREST_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.SMS_RFI_SUBMITTED]]:
    'SMS_RFI_SUBMITTED_RECEIVED',

  // PLATFORM
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_MOVED_TO_ACCEPTED
  ]]: 'PLATFORM_MOVED_TO_ACCEPTED_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_MOVED_TO_CONFIRMED
  ]]: 'PLATFORM_MOVED_TO_CONFIRMED_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.PLATFORM_MOVED_TO_OFFER]]:
    'PLATFORM_MOVED_TO_OFFER_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_SELLER_ACCEPT_OFFER
  ]]: 'PLATFORM_SELLER_ACCEPT_OFFER_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_SELLER_REJECT_OFFER
  ]]: 'PLATFORM_SELLER_REJECT_OFFER_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.PLATFORM_LISTING_SOLD]]:
    'PLATFORM_LISTING_SOLD_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_MESSAGE_FROM_SELLER
  ]]: 'PLATFORM_MESSAGE_FROM_SELLER_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_ACCESSIBLE_DOCUMENTS
  ]]: 'PLATFORM_ACCESSIBLE_DOCUMENTS_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.PLATFORM_NDA_SENT]]:
    'PLATFORM_NDA_SENT_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_LEAD_PULSE_CHECK
  ]]: 'PLATFORM_LEAD_PULSE_CHECK_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_LEAD_ADDED_TO_DEAL
  ]]: 'PLATFORM_LEAD_ADDED_TO_DEAL_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_QUESTION_DRAFTED
  ]]: 'PLATFORM_QUESTION_DRAFTED_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_QUESTION_REJECTED_BUY_SIDE
  ]]: 'PLATFORM_QUESTION_REJECTED_BUY_SIDE_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_QUESTION_REJECTED_SELL_SIDE
  ]]: 'PLATFORM_QUESTION_REJECTED_SELL_SIDE_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_QUESTION_REPLIED
  ]]: 'PLATFORM_QUESTION_REPLIED_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_QUESTION_EDITED
  ]]: 'PLATFORM_QUESTION_EDITED_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.PLATFORM_FILE_ACCESS]]:
    'PLATFORM_FILE_ACCESS_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_CONFIRM_FILES_ACCESS
  ]]: 'PLATFORM_CONFIRM_FILES_ACCESS_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_ENABLED_DATA_ROOM_ACCESS
  ]]: 'PLATFORM_ENABLED_DATA_ROOM_ACCESS_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_DOWNLOADED_FILE_EXPIRE_IN_TWO_DAYS
  ]]: 'PLATFORM_DOWNLOADED_FILE_EXPIRE_IN_TWO_DAYS_RECEIVED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_PENDING_FILES_REMINDER
  ]]: 'PLATFORM_PENDING_FILES_REMINDER_RECEIVED',
} as Record<number, string>;

export const LEAD_NOTIFICATIONS_TRANSLATIONS_OPENED = {
  // EMAIL
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_NDA_SENT]]:
    'EMAIL_NDA_SENT_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_NDA_RESENT]]:
    'EMAIL_NDA_RESENT_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_ACCEPTED_INTEREST]]:
    'EMAIL_ACCEPTED_INTEREST_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_ADDED_TO_DEAL]]:
    'EMAIL_ADDED_TO_DEAL_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.EMAIL_NEW_ACCESSIBLE_FILES
  ]]: 'EMAIL_NEW_ACCESSIBLE_FILES_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_NEW_FAQ]]:
    'EMAIL_NEW_FAQ_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.EMAIL_DATA_ROOM_ACCESS_APPROVED
  ]]: 'EMAIL_DATA_ROOM_ACCESS_APPROVED_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.EMAIL_DATA_ROOM_ACCESS_DECLINED
  ]]: 'EMAIL_DATA_ROOM_ACCESS_DECLINED_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_NUDGE]]:
    'EMAIL_NUDGE_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_RFI_SUBMITTED]]:
    'EMAIL_RFI_SUBMITTED_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_UNREAD_MESSAGES]]:
    'EMAIL_UNREAD_MESSAGES_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_QUESTION_ANSWERED]]:
    'EMAIL_QUESTION_ANSWERED_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.EMAIL_PENDING_FILES_REMINDER
  ]]: 'EMAIL_PENDING_FILES_REMINDER_OPENED',

  // SMS
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.SMS_CONFIRM_FILE_INTEREST
  ]]: 'SMS_CONFIRM_FILE_INTEREST_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.SMS_RFI_SUBMITTED]]:
    'SMS_RFI_SUBMITTED_OPENED',

  // PLATFORM
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_MOVED_TO_ACCEPTED
  ]]: 'PLATFORM_MOVED_TO_ACCEPTED_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_MOVED_TO_CONFIRMED
  ]]: 'PLATFORM_MOVED_TO_CONFIRMED_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.PLATFORM_MOVED_TO_OFFER]]:
    'PLATFORM_MOVED_TO_OFFER_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_SELLER_ACCEPT_OFFER
  ]]: 'PLATFORM_SELLER_ACCEPT_OFFER_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_SELLER_REJECT_OFFER
  ]]: 'PLATFORM_SELLER_REJECT_OFFER_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.PLATFORM_LISTING_SOLD]]:
    'PLATFORM_LISTING_SOLD_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_MESSAGE_FROM_SELLER
  ]]: 'PLATFORM_MESSAGE_FROM_SELLER_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_ACCESSIBLE_DOCUMENTS
  ]]: 'PLATFORM_ACCESSIBLE_DOCUMENTS_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.PLATFORM_NDA_SENT]]:
    'PLATFORM_NDA_SENT_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_LEAD_PULSE_CHECK
  ]]: 'PLATFORM_LEAD_PULSE_CHECK_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_LEAD_ADDED_TO_DEAL
  ]]: 'PLATFORM_LEAD_ADDED_TO_DEAL_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_QUESTION_DRAFTED
  ]]: 'PLATFORM_QUESTION_DRAFTED_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_QUESTION_REJECTED_BUY_SIDE
  ]]: 'PLATFORM_QUESTION_REJECTED_BUY_SIDE_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_QUESTION_REJECTED_SELL_SIDE
  ]]: 'PLATFORM_QUESTION_REJECTED_SELL_SIDE_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_QUESTION_REPLIED
  ]]: 'PLATFORM_QUESTION_REPLIED_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_QUESTION_EDITED
  ]]: 'PLATFORM_QUESTION_EDITED_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.PLATFORM_FILE_ACCESS]]:
    'PLATFORM_FILE_ACCESS_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_CONFIRM_FILES_ACCESS
  ]]: 'PLATFORM_CONFIRM_FILES_ACCESS_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_ENABLED_DATA_ROOM_ACCESS
  ]]: 'PLATFORM_ENABLED_DATA_ROOM_ACCESS_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_DOWNLOADED_FILE_EXPIRE_IN_TWO_DAYS
  ]]: 'PLATFORM_DOWNLOADED_FILE_EXPIRE_IN_TWO_DAYS_OPENED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_PENDING_FILES_REMINDER
  ]]: 'PLATFORM_PENDING_FILES_REMINDER_OPENED',
} as Record<number, string>;

export const LEAD_NOTIFICATIONS_TRANSLATIONS_FAILED = {
  // EMAIL
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_NDA_SENT]]:
    'EMAIL_NDA_SENT_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_NDA_RESENT]]:
    'EMAIL_NDA_RESENT_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_ACCEPTED_INTEREST]]:
    'EMAIL_ACCEPTED_INTEREST_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_ADDED_TO_DEAL]]:
    'EMAIL_ADDED_TO_DEAL_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.EMAIL_NEW_ACCESSIBLE_FILES
  ]]: 'EMAIL_NEW_ACCESSIBLE_FILES_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_NEW_FAQ]]:
    'EMAIL_NEW_FAQ_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.EMAIL_DATA_ROOM_ACCESS_APPROVED
  ]]: 'EMAIL_DATA_ROOM_ACCESS_APPROVED_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.EMAIL_DATA_ROOM_ACCESS_DECLINED
  ]]: 'EMAIL_DATA_ROOM_ACCESS_DECLINED_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_NUDGE]]:
    'EMAIL_NUDGE_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_RFI_SUBMITTED]]:
    'EMAIL_RFI_SUBMITTED_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_UNREAD_MESSAGES]]:
    'EMAIL_UNREAD_MESSAGES_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.EMAIL_QUESTION_ANSWERED]]:
    'EMAIL_QUESTION_ANSWERED_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.EMAIL_PENDING_FILES_REMINDER
  ]]: 'EMAIL_PENDING_FILES_REMINDER_FAILED',

  // SMS
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.SMS_CONFIRM_FILE_INTEREST
  ]]: 'SMS_CONFIRM_FILE_INTEREST_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.SMS_RFI_SUBMITTED]]:
    'SMS_RFI_SUBMITTED_FAILED',

  // PLATFORM
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_MOVED_TO_ACCEPTED
  ]]: 'PLATFORM_MOVED_TO_ACCEPTED_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_MOVED_TO_CONFIRMED
  ]]: 'PLATFORM_MOVED_TO_CONFIRMED_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.PLATFORM_MOVED_TO_OFFER]]:
    'PLATFORM_MOVED_TO_OFFER_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_SELLER_ACCEPT_OFFER
  ]]: 'PLATFORM_SELLER_ACCEPT_OFFER_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_SELLER_REJECT_OFFER
  ]]: 'PLATFORM_SELLER_REJECT_OFFER_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.PLATFORM_LISTING_SOLD]]:
    'PLATFORM_LISTING_SOLD_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_MESSAGE_FROM_SELLER
  ]]: 'PLATFORM_MESSAGE_FROM_SELLER_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_ACCESSIBLE_DOCUMENTS
  ]]: 'PLATFORM_ACCESSIBLE_DOCUMENTS_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.PLATFORM_NDA_SENT]]:
    'PLATFORM_NDA_SENT_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_LEAD_PULSE_CHECK
  ]]: 'PLATFORM_LEAD_PULSE_CHECK_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_LEAD_ADDED_TO_DEAL
  ]]: 'PLATFORM_LEAD_ADDED_TO_DEAL_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_QUESTION_DRAFTED
  ]]: 'PLATFORM_QUESTION_DRAFTED_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_QUESTION_REJECTED_BUY_SIDE
  ]]: 'PLATFORM_QUESTION_REJECTED_BUY_SIDE_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_QUESTION_REJECTED_SELL_SIDE
  ]]: 'PLATFORM_QUESTION_REJECTED_SELL_SIDE_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_QUESTION_REPLIED
  ]]: 'PLATFORM_QUESTION_REPLIED_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_QUESTION_EDITED
  ]]: 'PLATFORM_QUESTION_EDITED_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[LEAD_NOTIFICATION_TYPE.PLATFORM_FILE_ACCESS]]:
    'PLATFORM_FILE_ACCESS_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_CONFIRM_FILES_ACCESS
  ]]: 'PLATFORM_CONFIRM_FILES_ACCESS_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_ENABLED_DATA_ROOM_ACCESS
  ]]: 'PLATFORM_ENABLED_DATA_ROOM_ACCESS_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_DOWNLOADED_FILE_EXPIRE_IN_TWO_DAYS
  ]]: 'PLATFORM_DOWNLOADED_FILE_EXPIRE_IN_TWO_DAYS_FAILED',
  [LEAD_NOTIFICATION_TYPE_CODE[
    LEAD_NOTIFICATION_TYPE.PLATFORM_PENDING_FILES_REMINDER
  ]]: 'PLATFORM_PENDING_FILES_REMINDER_FAILED',
} as Record<number, string>;
